import React from 'react'
import { FormattedNumber, injectIntl, InjectedIntlProps } from 'react-intl'
import cx from 'classnames'
import SummarySection from 'components/SummarySection/SummarySection'
import { PieChart, PieChartLegends } from 'components/PieChart/index'
import TitledContent from 'components/TitledContent/TitledContent'
import ConditionalWrapper from 'components/ConditionalWrapper/ConditionalWrapper'
import Income from 'components/Income/Income'
import Icon from 'components/Icon/Icon'
import NoData from 'components/NoData/NoData'
import BoxDecoration from 'components/BoxDecoration/BoxDecoration'
import messages from './messages'
import AntdTooltip from 'UI/Tooltip/Tooltip'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useCurrency } from 'contexts'
import styles from './BusinessesSummary.scss'

interface EntityItem {
  key: string
  icon?: string
}

const ENTITIES: EntityItem[] = [
  {
    key: 'total',
  },
  {
    key: 'nonFiler',
    icon: 'nonFiler',
  },
  {
    key: 'underReport',
    icon: 'underReport',
  },
]

const INCOME_TYPES_ESTIMATED = ['estimated', 'reported', 'untaxed'] as const
const INCOME_TYPES_DOCUMENTED = ['documented', 'reported', 'untaxed'] as const
const INCOME_TYPES_TOTAL = ['total', 'reported', 'untaxed'] as const

const INCOME_TYPES_CRYPTO = ['profitLoss', 'reported', 'untaxed'] as const

const topIndustriesLegendsColors = ['hsl(149, 70%, 50%)', 'hsl(29, 70%, 50%)', 'hsl(27, 70%, 50%)', 'hsl(11, 70%, 50%)']

const mapIncomeKey = (key: string, isCrypto: boolean): string => {
  if (isCrypto && key === 'profitLoss') {
    return 'total'
  }
  return key
}

interface BusinessesSummaryProps extends InjectedIntlProps {
  isLoading?: boolean
  summary: {
    entityCounts?: Record<string, number>
    incomeCounts?: Record<string, number>
    topIndustries?: [string, number][]
    industriesCount?: number
  }
  isCrypto?: boolean
  isInfluencers?: boolean
}

function BusinessesSummary({ intl, isLoading = false, summary, isCrypto = false, isInfluencers = false }: BusinessesSummaryProps) {
  const { entityCounts, incomeCounts, topIndustries = [], industriesCount } = summary
  const { currentCurrency } = useCurrency()

  const income_types = isCrypto ? INCOME_TYPES_CRYPTO : isInfluencers ? INCOME_TYPES_TOTAL : INCOME_TYPES_ESTIMATED

  const divider = industriesCount || entityCounts?.total

  const topIndustriesLegends = Array.isArray(topIndustries)
    ? (topIndustries || [])?.slice(0, 4)?.map(([id, value], index) => ({
        id,
        value: value / (divider || 1),
        label: id,
        color: topIndustriesLegendsColors[index],
      }))
    : []

  return (
    <div className={styles.container}>
      <BoxDecoration />
      <SummarySection name='entities' isLoading={isLoading}>
        {ENTITIES
          .filter(({ key }) => !isCrypto || key === 'total' || !!entityCounts?.[key])
          .map(({ key = '', icon = '' }) => (
          <TitledContent
            key={key}
            title={intl.formatMessage((messages.entities as any)[key])}
            className={cx(styles.entity, styles[key])}
            titleClassName={styles.sectionTitle}
            contentClassName=""
          >
            <ConditionalWrapper
              condition={!!icon && !!entityCounts?.[key]}
              wrapper={(children: React.ReactElement) => (
                <Icon name={icon} direction='right' size='regular' className={styles.entityIcon}>
                  {children}
                </Icon>
              )}
            >
              {entityCounts?.[key] ? (
                <span className={cx({ [styles.hidden]: !entityCounts[key] })}>
                  <FormattedNumber value={entityCounts[key] || 0} />
                </span>
              ) : (
                <NoData />
              )}
            </ConditionalWrapper>
          </TitledContent>
        ))}
      </SummarySection>
      <SummarySection name='income' isLoading={isLoading}>
        {income_types
          .filter(key => {
            if (!isCrypto) return true
            const dk = mapIncomeKey(key, isCrypto)
            const val = incomeCounts?.[dk]
            return typeof val === 'number' && val !== 0
          })
          .map(key => {
          const displayKey = mapIncomeKey(key, isCrypto)

          return (
            <TitledContent
              key={key}
              title={
                <>
                  {key === 'profitLoss' && isCrypto ? `Gain/Loss (${currentCurrency})` : intl.formatMessage((messages.incomes as any)[key])}
                  {key === 'profitLoss' && isCrypto && (
                    <AntdTooltip
                      title='Total revenue from crypto activity, such as sales, mints, and royalties, minus fees and cost basis (initial purchase price of the asset)'
                      placement='bottom'
                      color='#fff'
                      header={`Gain/Loss (${currentCurrency})`}
                    >
                      <InfoCircleOutlined style={{ color: '#fff', marginLeft: '6px' }} />
                    </AntdTooltip>
                  )}
                  {key === 'reported' && (
                    <AntdTooltip
                      title='Total business activity reported on tax filings by these entities.'
                      placement='bottom'
                      color='#fff'
                      header={intl.formatMessage((messages.incomes as any)[key])}
                    >
                      <InfoCircleOutlined style={{ color: '#fff', marginLeft: '6px' }} />
                    </AntdTooltip>
                  )}
                  {key === 'untaxed' && (
                    <AntdTooltip
                      title='Difference between estimated (total) and reported revenue. This would indicate potential tax noncompliance.'
                      placement='bottom'
                      color='#fff'
                      header={intl.formatMessage((messages.incomes as any)[key])}
                    >
                      <InfoCircleOutlined style={{ color: '#fff', marginLeft: '6px' }} />
                    </AntdTooltip>
                  )}
                  {key === 'total' && (
                    <AntdTooltip
                      title='Sum of revenue across all entities found by IVIX.'
                      placement='bottom'
                      color='#fff'
                      header={intl.formatMessage((messages.incomes as any)[key])}
                    >
                      <InfoCircleOutlined style={{ color: '#fff', marginLeft: '6px' }} />
                    </AntdTooltip>
                  )}
                </>
              }
              className={styles.income}
              titleClassName={styles.sectionTitle}
              contentClassName=""
            >
              {typeof incomeCounts?.[displayKey] === 'number' ? (
                <Income value={incomeCounts[displayKey] || 0} useCurrencyConversion={true} />
              ) : (
                <NoData />
              )}
            </TitledContent>
          )
        })}
      </SummarySection>
      <SummarySection
        name={isCrypto ? 'platforms' : isInfluencers ? 'revenueSources' : 'industries'}
        isLoading={isLoading}
      >
        <PieChart data={topIndustriesLegends} className={styles.pie} innerClassName={styles.totalResolvers} />
        <PieChartLegends legends={topIndustriesLegends} />
      </SummarySection>
    </div>
  )
}

export default injectIntl(BusinessesSummary)

